import { useQuery } from '@tanstack/react-query';
import { Loading } from 'corso-admin-ui';
import { CrewMerchantUi } from 'corso-types';
import { createContext, PropsWithChildren, useContext } from 'react';
import { z } from 'zod';
import api from '~/api';
import PageStatus from '~/components/PageStatus';
import { usePathParams } from '~/hooks/usePathParams';
import { useStoreId } from '~/providers/StoreUserProvider';

function useShippingClaimIdParam() {
  return usePathParams(
    z.object({ shippingClaimId: z.coerce.number().optional() }),
  ).shippingClaimId;
}

export const shippingClaimQueryKey = (
  shippingClaimId: number | undefined,
  storeId: string,
) => ['shippingClaim', shippingClaimId, storeId] as const;

function useShippingClaimQuery() {
  const shippingClaimId = useShippingClaimIdParam();
  const storeId = useStoreId();

  return useQuery({
    enabled: !!shippingClaimId,
    throwOnError: false,
    queryKey: shippingClaimQueryKey(shippingClaimId, storeId),
    queryFn: () => {
      if (!shippingClaimId) throw new Error('Missing Shipping Claim ID');
      return api
        .store(storeId)
        .shippingProtection.claim(`${shippingClaimId}`)
        .get();
    },
  });
}

const ShippingClaimContext = createContext<CrewMerchantUi.ShippingClaim | null>(
  null,
);

export function useShippingClaim() {
  const claim = useContext(ShippingClaimContext);
  if (!claim) {
    throw new Error(
      'useShippingClaim must be used within a ShippingClaimReviewProvider',
    );
  }
  return claim;
}

export function ShippingClaimReviewProvider({ children }: PropsWithChildren) {
  const { data: claim, error, isError, isPending } = useShippingClaimQuery();

  if (isError) {
    return <PageStatus.Error error={error} />;
  }

  if (isPending) {
    return (
      <>
        <Loading />
        <PageStatus.Loading />
      </>
    );
  }

  return (
    <ShippingClaimContext.Provider value={claim}>
      {children}
    </ShippingClaimContext.Provider>
  );
}
